import { 
  Component, 
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  SimpleChanges,
  EventEmitter,
  Output,
  ViewChild,
  Inject,
  forwardRef
} from '@angular/core';

import { 
  FormGroup,
  FormControl,
  Validators 
} from '@angular/forms';

import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil, mergeMap, shareReplay } from 'rxjs/operators';
import { DatexFormControl, validateControlOnChange, validateFormOnControlChange } from './models/datex-form-control';
import { TabItemModel, TabGroupModel } from './models/tab';
import { WidgetModel } from './models/widget';
import { 
  TextBoxModel, 
  NumberBoxModel, 
  SelectBoxModel, 
  ESelectBoxType,
  DateBoxModel, 
  CheckBoxModel, 
  TextModel, 
  LabelModel, 
  ButtonModel,
  SplitButtonModel,
  SeparatorModel,
  ImageModel,
  DrawModel,
  CodeBoxModel,
  ButtonStyles,
  ValueControlModel
} from './models/control';
import { Styles, ControlContainerStyles } from './models/style';
import { FieldModel } from './models/field';
import { FieldsetModel } from './models/fieldset';
import { ToolModel } from './models/tool';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { FootPrintPortalManager_ShellService } from './FootPrintPortalManager.shell.service';
import { FootPrintPortalManager_OperationService } from './FootPrintPortalManager.operation.service';
import { FootPrintPortalManager_DatasourceService } from './FootPrintPortalManager.datasource.index';
import { FootPrintPortalManager_FlowService } from './FootPrintPortalManager.flow.index';
import { FootPrintPortalManager_ReportService } from './FootPrintPortalManager.report.index';
import { FootPrintPortalManager_LocalizationService } from './FootPrintPortalManager.localization.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './FootPrintPortalManager.frontend.types'
import { $frontendTypes as $types} from './FootPrintPortalManager.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';


import { FootPrintPortalManager_shipping_order_lines_gridComponent } from './FootPrintPortalManager.shipping_order_lines_grid.component';
import { FootPrintPortalManager_orderaddresses_gridComponent } from './FootPrintPortalManager.orderaddresses_grid.component';
import { FootPrintPortalManager_instructions_gridComponent } from './FootPrintPortalManager.instructions_grid.component';
import { FootPrintPortalManager_shippingcontents_gridComponent } from './FootPrintPortalManager.shippingcontents_grid.component';
import { FootPrintPortalManager_shipping_order_tasks_gridComponent } from './FootPrintPortalManager.shipping_order_tasks_grid.component';
import { SalesOrders_order_total_units_widgetComponent } from './SalesOrders.order_total_units_widget.component';
import { SalesOrders_order_total_picked_units_widgetComponent } from './SalesOrders.order_total_picked_units_widget.component';
import { SalesOrders_order_total_picked_gross_widgetComponent } from './SalesOrders.order_total_picked_gross_widget.component';
import { Carriers_carriers_ownerscarriers_dd_singleComponent } from './Carriers.carriers_ownerscarriers_dd_single.component'
import { Carriers_carrierservicetype_dd_singleComponent } from './Carriers.carrierservicetype_dd_single.component'

type EntityType = { 
    Id?: number, AccountId?: number, BillingAddresId?: number, LookupCode?: string, Notes?: string, OrderClassId?: number, OrderStatusId?: number, OwnerReference?: string, PreferredCarrierId?: number, PreferredCarrierServiceTypeId?: number, PreferredWarehouseId?: number, ProjectId?: number, RequestedDeliveryDate?: string, VendorReference?: string, Project?: { LookupCode?: string, OwnerId?: number, Owner?: { LookupCode?: string } }, OrderClass?: { LookupCode?: string, Name?: string }, Status?: { Name?: string }, ShipmentOrderLookups?: { ShipmentId?: number, Shipment?: { Id?: number, BillOfLading?: string, ContainerIdentifier?: string, ConveyanceId?: number, ConveyenceReference?: string, SealId?: string, TrailerId?: string } }[], OrderLines?: { LineNumber?: number }[], Account?: { Id?: number, AccountsContactsLookup?: { ContactId?: number, Contact?: { FirstName?: string, LastName?: string, Address?: { City?: string, Country?: string, Line1?: string, Line2?: string, PostalCode?: string, State?: string } } }[] }, PreferredWarehouse?: { Name?: string }}; 

@Component({
  standalone: true,
  imports: [
    SharedModule,
    forwardRef(() => FootPrintPortalManager_shipping_order_lines_gridComponent),
    forwardRef(() => FootPrintPortalManager_orderaddresses_gridComponent),
    forwardRef(() => FootPrintPortalManager_instructions_gridComponent),
    forwardRef(() => FootPrintPortalManager_shippingcontents_gridComponent),
    forwardRef(() => FootPrintPortalManager_shipping_order_tasks_gridComponent),
    forwardRef(() => SalesOrders_order_total_units_widgetComponent),
    forwardRef(() => SalesOrders_order_total_picked_units_widgetComponent),
    forwardRef(() => SalesOrders_order_total_picked_gross_widgetComponent),
    forwardRef(() => Carriers_carriers_ownerscarriers_dd_singleComponent),
    forwardRef(() => Carriers_carrierservicetype_dd_singleComponent),
  ],
  selector: 'FootPrintPortalManager-shipping_order_editor',
  templateUrl: './FootPrintPortalManager.shipping_order_editor.component.html'
})
export class FootPrintPortalManager_shipping_order_editorComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  inParams: { orderId: number } = { orderId: null };
  //#region Inputs
  @Input('orderId') set $inParams_orderId(v: number) {
    this.inParams.orderId = v;
  }
  get $inParams_orderId(): number {
    return this.inParams.orderId;
  }
  //#endregion Inputs

  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  @Output()
  $refreshEvent = new EventEmitter();
  //#endregion Outputs

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  vars: { shipmentId?: number, billingAddressId?: number, orderState?: number } = { };
  //#endregion
  entity: EntityType;

  formGroup: FormGroup = new FormGroup({
    reference: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    vendor_reference: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    expected_date: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    container_identifier: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    carrier: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    carrier_service: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    notes: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
  });
  
  get valid(): boolean {
    return this.formGroup.valid;
  }

  toolbar = {
      submit: new ToolModel(new ButtonModel('submit', new ButtonStyles(null, null), false, false, 'Submit order', 'icon-ic_fluent_checkmark_circle_20_regular', null)
    , false),
      appointment: new ToolModel(new ButtonModel('appointment', new ButtonStyles(null, null), false, false, 'Appointment', 'icon-ic_fluent_calendar_20_regular', null)
    , false),
      separator1: new ToolModel(new SeparatorModel(new Styles(null, null))
    , false),
      cancel: new ToolModel(new ButtonModel('cancel', new ButtonStyles(null, null), false, false, 'Cancel order', 'icon-ic_fluent_dismiss_circle_20_regular', null)
    , false),
      on_delete: new ToolModel(new ButtonModel('on_delete', new ButtonStyles(null, null), false, false, ' Delete order', 'icon-ic_fluent_delete_20_regular', null)
    , false),
      separator2: new ToolModel(new SeparatorModel(new Styles(null, null))
    , false),
      attachments: new ToolModel(new ButtonModel('attachments', new ButtonStyles(null, null), false, false, ' ', 'icon-ic_fluent_attach_20_regular', null)
    , false),
      print: new ToolModel(new ButtonModel('print', new ButtonStyles(null, null), false, false, ' ', 'icon-ic_fluent_print_20_regular', null)
    , false),
      discussions: new ToolModel(new ButtonModel('discussions', new ButtonStyles(null, null), false, false, ' ', 'icon-ic_fluent_chat_20_regular', null)
    , false),
      separator3: new ToolModel(new SeparatorModel(new Styles(null, null))
    , false),
      options: new ToolModel(new SplitButtonModel<{ email_requests: ButtonModel, ship_to: ButtonModel }>(
        'options',
        new ButtonStyles(null, null),
        false,
        ' ',
        'icon-ic_fluent_more_horizontal_20_regular',
        null,
        [
          new ButtonModel('email_requests', new ButtonStyles(null, null), false, false, 'Email requests', 'icon-ic_fluent_mail_arrow_up_20_regular'),
          new ButtonModel('ship_to', new ButtonStyles(null, null), false, false, 'Ship to contact book', 'icon-ic_fluent_contact_card_20_regular')
        ])
    , false)
  };

  fields = {
    order_header: new FieldModel(new TextModel(null, null, null, null, null)
, new ControlContainerStyles(null, null), 'Order header', false, false),
    order_state: new FieldModel(new TextModel(null, null, null, null, null)
, new ControlContainerStyles(null, null), 'Order state', false, false),
    ship_to: new FieldModel(new TextModel(null, null, null, null, null)
, new ControlContainerStyles(null, null), 'Ship to contact', false, false),
    clear_ship_to: new FieldModel(new ButtonModel('', new ButtonStyles(null, null), false, false, 'Clear', 'ms-Icon ms-Icon--Cancel', null)
, new ControlContainerStyles(null, null), '', false, false),
    reference: new FieldModel(new TextBoxModel(this.formGroup.controls['reference'] as DatexFormControl, null, false, '', null)
, new ControlContainerStyles(null, null), 'Owner reference', false, false),
    vendor_reference: new FieldModel(new TextBoxModel(this.formGroup.controls['vendor_reference'] as DatexFormControl, null, false, '', null)
, new ControlContainerStyles(null, null), 'Vendor reference', false, false),
    expected_date: new FieldModel(new DateBoxModel(this.formGroup.controls['expected_date'] as DatexFormControl, null, false, 'MM/DD/YYYY', 'datetime', null)
, new ControlContainerStyles(null, null), 'Expected date', false, false),
    container_identifier: new FieldModel(new TextBoxModel(this.formGroup.controls['container_identifier'] as DatexFormControl, null, true, '', null)
, new ControlContainerStyles(null, null), 'Container identifier', false, false),
    carrier: new FieldModel(new SelectBoxModel(
  this.formGroup.controls['carrier'] as DatexFormControl, 
  null, null,
  false, 
  '', null)
, new ControlContainerStyles(null, null), 'Carrier', false, false),
    carrier_service: new FieldModel(new SelectBoxModel(
  this.formGroup.controls['carrier_service'] as DatexFormControl, 
  null, null,
  false, 
  '', null)
, new ControlContainerStyles(null, null), 'Service', false, false),
    notes: new FieldModel(new TextBoxModel(this.formGroup.controls['notes'] as DatexFormControl, null, false, '', null)
, new ControlContainerStyles(null, null), 'Notes', false, false),
  };

  fieldsets = {
  newGroup1: new FieldsetModel('', true, false, true, false),
  newGroup2: new FieldsetModel('Details', false, true, false, false),
};

    rootTabGroup = new TabGroupModel();
  
    subTabGroups = {
    };
  
    onTabSelected(event: MatSelectChange) {
      event.value.activate();
    }
  
    tabs = {
      sales_order_lines: new TabItemModel(
        this.rootTabGroup, 
        'Shipping lines', 
        false,
        ),
      contacts: new TabItemModel(
        this.rootTabGroup, 
        'Contacts', 
        false,
        ),
      instructions: new TabItemModel(
        this.rootTabGroup, 
        'Instructions', 
        false,
        ),
      shipping_contents: new TabItemModel(
        this.rootTabGroup, 
        'Picked', 
        false,
        ),
      tasks: new TabItemModel(
        this.rootTabGroup, 
        'Activity', 
        false,
        ),
    };
  
  
    //#region tabs inParams
    get $tabs_sales_order_lines_shipping_order_lines_grid_inParams_orderId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $tabs_sales_order_lines_shipping_order_lines_grid_inParams_shipmentId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.entity.ShipmentOrderLookups[0]?.ShipmentId;
      
      return expr;
    }
  
    get $tabs_sales_order_lines_shipping_order_lines_grid_inParams_projectId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.entity.ProjectId;
      
      return expr;
    }
  
    get $tabs_sales_order_lines_shipping_order_lines_grid_inParams_orderStatusId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.entity.OrderStatusId;
      
      return expr;
    }
  
    get $tabs_sales_order_lines_shipping_order_lines_grid_inParams_warehouseId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.entity.PreferredWarehouseId;
      
      return expr;
    }
  
    get $tabs_sales_order_lines_shipping_order_lines_grid_inParams_orderState(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.vars.orderState;
      
      return expr;
    }
  
    get $tabs_contacts_orderaddresses_grid_inParams_orderId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $tabs_instructions_instructions_grid_inParams_entityName(): string {
      const $editor = this;
      const $utils = this.utils;
      const expr = 'Order';
      
      return expr;
    }
  
    get $tabs_instructions_instructions_grid_inParams_enabled(): boolean {
      const $editor = this;
      const $utils = this.utils;
      const expr = true;
      
      return expr;
    }
  
    get $tabs_instructions_instructions_grid_inParams_entityKey1(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $tabs_shipping_contents_shippingcontents_grid_inParams_orderId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $tabs_shipping_contents_shippingcontents_grid_inParams_shipmentId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.vars.shipmentId;
      
      return expr;
    }
  
    get $tabs_tasks_shipping_order_tasks_grid_inParams_orderId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $tabs_tasks_shipping_order_tasks_grid_inParams_shipmentId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.entity.ShipmentOrderLookups[0]?.ShipmentId;
      
      return expr;
    }
  
    //#endregion tabs inParams
  
    //#region tabs children
      @ViewChild('$tabs_sales_order_lines', { read: FootPrintPortalManager_shipping_order_lines_gridComponent }) $tabs_sales_order_lines: FootPrintPortalManager_shipping_order_lines_gridComponent;
      @ViewChild('$tabs_contacts', { read: FootPrintPortalManager_orderaddresses_gridComponent }) $tabs_contacts: FootPrintPortalManager_orderaddresses_gridComponent;
      @ViewChild('$tabs_instructions', { read: FootPrintPortalManager_instructions_gridComponent }) $tabs_instructions: FootPrintPortalManager_instructions_gridComponent;
      @ViewChild('$tabs_shipping_contents', { read: FootPrintPortalManager_shippingcontents_gridComponent }) $tabs_shipping_contents: FootPrintPortalManager_shippingcontents_gridComponent;
      @ViewChild('$tabs_tasks', { read: FootPrintPortalManager_shipping_order_tasks_gridComponent }) $tabs_tasks: FootPrintPortalManager_shipping_order_tasks_gridComponent;
    //#endregion tabs children
    widgets = {
      order_total_units_widget: new WidgetModel(false),
      order_total_picked_units_widget: new WidgetModel(false),
      order_total_picked_gross_widget: new WidgetModel(false),
    };
  
    //#region widgets inParams
    get $widgets_order_total_units_widget_inParams_orderId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $widgets_order_total_picked_units_widget_inParams_orderId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $widgets_order_total_picked_units_widget_inParams_shipmentId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.vars.shipmentId;
      
      return expr;
    }
  
    get $widgets_order_total_picked_gross_widget_inParams_orderId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.inParams.orderId;
      
      return expr;
    }
  
    get $widgets_order_total_picked_gross_widget_inParams_shipmentId(): number {
      const $editor = this;
      const $utils = this.utils;
      const expr = $editor.vars.shipmentId;
      
      return expr;
    }
  
    //#endregion widgets inParams
  
    //#region widgets children
      @ViewChild('$widgets_order_total_units_widget', { read:  SalesOrders_order_total_units_widgetComponent }) $widgets_order_total_units_widget: SalesOrders_order_total_units_widgetComponent;
      @ViewChild('$widgets_order_total_picked_units_widget', { read:  SalesOrders_order_total_picked_units_widgetComponent }) $widgets_order_total_picked_units_widget: SalesOrders_order_total_picked_units_widgetComponent;
      @ViewChild('$widgets_order_total_picked_gross_widget', { read:  SalesOrders_order_total_picked_gross_widgetComponent }) $widgets_order_total_picked_gross_widget: SalesOrders_order_total_picked_gross_widgetComponent;
    //#endregion widgets children

  //#region fields inParams
  get $fields_carrier_selector_inParams_ownerId(): number {
    if (!this.entity) return null; 
    const $editor = this;
    const $utils = this.utils;
    const expr = $editor.entity.Project.OwnerId;
    
    return expr;
  }

  get $fields_carrier_service_selector_inParams_carrierId(): number {
    if (!this.entity) return null; 
    const $editor = this;
    const $utils = this.utils;
    const expr = $editor.fields.carrier.control.value;
    
    return expr;
  }

  //#endregion fields inParams

  $formGroupFieldValidationObservables = {
    reference: this.fields.reference.control.valueChanges
    ,
    vendor_reference: this.fields.vendor_reference.control.valueChanges
    ,
    expected_date: this.fields.expected_date.control.valueChanges
    ,
    container_identifier: this.fields.container_identifier.control.valueChanges
    ,
    carrier: this.fields.carrier.control.valueChanges
    ,
    carrier_service: this.fields.carrier_service.control.valueChanges
    ,
    notes: this.fields.notes.control.valueChanges
    ,
  }
  

  constructor(
    private utils: UtilsService,
    private settings: SettingsValuesService,
    private shell: FootPrintPortalManager_ShellService,
    private datasources: FootPrintPortalManager_DatasourceService,
    private flows: FootPrintPortalManager_FlowService,
    private reports: FootPrintPortalManager_ReportService,
    private localization: FootPrintPortalManager_LocalizationService,
    private operations: FootPrintPortalManager_OperationService,
    private logger: CleanupLoggerService,
    ) { 
    super();
    this.$subscribeFormControlValueChanges();
    
    //#region tabs tab init
    this.rootTabGroup.tabs = [
      this.tabs.sales_order_lines,
      this.tabs.contacts,
      this.tabs.instructions,
      this.tabs.shipping_contents,
      this.tabs.tasks,
    ]; 
    //#endregion tabs tab init
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }
  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.orderId)) {
        this.$missingRequiredInParams.push('orderId');
      }
  }

  initialized = false;

  $hasDataLoaded = false;

  async $init() {
    this.title = 'Edit Shipping Order';
    
    await this.on_init();

    await this.$dataLoad();
    this.initialized = true;
  }

  async $dataLoad() {
    const $editor = this;
    const $utils = this.utils;

    const dsParams = {
      orderId:  $editor.inParams.orderId 
    };

    const data = await this.datasources.FootPrintPortalManager.ds_shipping_order_editor.get(dsParams);

    if (isNil(data.result)) {
      this.$hasDataLoaded = false;
      this.entity = null;
    } else {
      this.$hasDataLoaded = true;

      await this.$applyLinkedDatasourcesAndCustomColumns(dsParams, data);
      
      this.entity = data.result as EntityType;

      await this.$dataLoaded();
    }
  }

  
    private async $applyLinkedDatasourcesAndCustomColumns(inParams: any, outParams: any) {
      const $datasource = { inParams: inParams };
      const $utils = this.utils;
  
    }

  async $dataLoaded() {
    const $editor = this;
    const $utils = this.utils;
   
    (this.fields.reference.control as TextBoxModel).reset($editor.entity.OwnerReference);
    (this.fields.vendor_reference.control as TextBoxModel).reset($editor.entity.VendorReference);
    (this.fields.expected_date.control as DateBoxModel).reset($editor.entity.RequestedDeliveryDate);
    (this.fields.container_identifier.control as TextBoxModel).reset($editor.entity.ShipmentOrderLookups[0]?.Shipment?.ContainerIdentifier);
    (this.fields.carrier.control as SelectBoxModel).reset($editor.entity.PreferredCarrierId);
    (this.fields.carrier_service.control as SelectBoxModel).reset($editor.entity.PreferredCarrierServiceTypeId);
    (this.fields.notes.control as TextBoxModel).reset($editor.entity.Notes);

    await this.on_data_loaded();
  }

  refresh(
    skipParent = false,
    skipChildren = false,
    childToSkip: string = null) {
    if (this.$hasMissingRequiredInParams) {
      return Promise.resolve(null);
    }
    // up
    if (skipParent === false) {
      this.$refreshEvent.emit();
    }

    // self
    const result = this.$dataLoad();
    
    // children
    if (skipChildren === false) {
      this.$refreshChildren(childToSkip);
    }

    return result;
  }

  $refreshChildren(childToSkip: string) {
    //#region widgets children
    if (childToSkip !== '$widgets_order_total_units_widget') {
      if (!isNil(this.$widgets_order_total_units_widget) && !this.widgets.order_total_units_widget.hidden && !this.widgets.order_total_units_widget.removed) {
        this.$widgets_order_total_units_widget.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$widgets_order_total_picked_units_widget') {
      if (!isNil(this.$widgets_order_total_picked_units_widget) && !this.widgets.order_total_picked_units_widget.hidden && !this.widgets.order_total_picked_units_widget.removed) {
        this.$widgets_order_total_picked_units_widget.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$widgets_order_total_picked_gross_widget') {
      if (!isNil(this.$widgets_order_total_picked_gross_widget) && !this.widgets.order_total_picked_gross_widget.hidden && !this.widgets.order_total_picked_gross_widget.removed) {
        this.$widgets_order_total_picked_gross_widget.refresh(true, false, null);
      }
    }
    //#endregion widgets children
    //#region tabs children
      if (childToSkip !== '$tabs_sales_order_lines') {
        if (!isNil(this.$tabs_sales_order_lines) && !this.tabs.sales_order_lines.hidden && !this.tabs.sales_order_lines.removed) {
          this.$tabs_sales_order_lines.refresh(true, false, null);
        }
      }    
      if (childToSkip !== '$tabs_contacts') {
        if (!isNil(this.$tabs_contacts) && !this.tabs.contacts.hidden && !this.tabs.contacts.removed) {
          this.$tabs_contacts.refresh(true, false, null);
        }
      }    
      if (childToSkip !== '$tabs_instructions') {
        if (!isNil(this.$tabs_instructions) && !this.tabs.instructions.hidden && !this.tabs.instructions.removed) {
          this.$tabs_instructions.refresh(true, false, null);
        }
      }    
      if (childToSkip !== '$tabs_shipping_contents') {
        if (!isNil(this.$tabs_shipping_contents) && !this.tabs.shipping_contents.hidden && !this.tabs.shipping_contents.removed) {
          this.$tabs_shipping_contents.refresh(true, false, null);
        }
      }    
      if (childToSkip !== '$tabs_tasks') {
        if (!isNil(this.$tabs_tasks) && !this.tabs.tasks.hidden && !this.tabs.tasks.removed) {
          this.$tabs_tasks.refresh(true, false, null);
        }
      }    
    //#endregion tabs children
  }

  close() {
    this.$finish.emit();
  }

  openImageViewer(imageSource: string) {
    this.shell.openImageViewerDialog(imageSource);
  }
  
  private $subscribeFormControlValueChanges() {
    this.$formGroupFieldValidationObservables
      .reference
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_save();
      });
    this.$formGroupFieldValidationObservables
      .vendor_reference
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_save();
      });
    this.$formGroupFieldValidationObservables
      .expected_date
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_save();
      });
    this.$formGroupFieldValidationObservables
      .container_identifier
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_save();
      });
    this.$formGroupFieldValidationObservables
      .carrier
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_save();
      });
    this.$formGroupFieldValidationObservables
      .carrier_service
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_save();
      });
    this.$formGroupFieldValidationObservables
      .notes
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_save();
      });
  }

  //#region private flows
  on_delete_clicked(event = null) {
    return this.on_delete_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_delete_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  const result = await $flows.SalesOrders.is_sales_order_deletable_flow({ orderId: $editor.entity.Id });
  const reason = result.reason;
  if ($utils.isDefined(reason)) {
      await $shell.FootPrintPortalManager.openErrorDialog('Order cannot be deleted.', reason);
      return;
  } else {
      const confirm = await $shell.FootPrintPortalManager.openConfirmationDialog('Delete Order', `Are you sure you want to delete order  ${$editor.entity.LookupCode}?`, 'Yes', 'No');
      if (confirm) {
          try {
              const orderLookup = $editor.entity.LookupCode;
              const result = await $flows.SalesOrders.delete_sales_order_flow({ orderId: $editor.entity.Id });
              const reason = result.reason;
  
              if ($utils.isDefined(reason)) {
                  await $shell.FootPrintPortalManager.openErrorDialog('Order cannot be deleted.', reason);
                  return;
              }
              else {
                  const title = 'Order Deleted'
                  const message = `Order ${orderLookup} successfully deleted. `
                  $shell.FootPrintPortalManager.openToaster(title, message, EToasterType.Success, { timeOut: 5000, positionClass: EToasterPosition.topRight });
                  $editor.close();
              }
          } catch (error) {
              const errorMessage = $utils.isDefined(error?.error?.error) ? error?.error?.error.message : error;
              const errorDetail = $utils.isDefined(error?.error?.error) ? error?.error?.error : error;
              const errorDescription = `Order ${$editor.entity.LookupCode} - ${errorMessage}`;
              await $shell.FootPrintPortalManager.openErrorDialog('Order delete error', 'An error occurred during the deletion of the order', [errorDescription], null, [{ message: errorDescription, detail: errorDetail }]);
          }
      }
  }
  }
  on_cancel_clicked(event = null) {
    return this.on_cancel_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_cancel_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  var shipmentId = $editor.entity.ShipmentOrderLookups[0]?.ShipmentId;
  var orderId = $editor.inParams.orderId;
  var orderStatusId = $editor.entity.OrderStatusId;
  
  if ($utils.isDefined(shipmentId)) {
  
      const result = await $flows.SalesOrders.is_sales_order_cancelable_flow({ orderId: orderId, shipmentId: shipmentId });
      const reason = result.reason;
      if ($utils.isDefined(reason)) {
          await $shell.FootPrintPortalManager.openErrorDialog('Order cannot be canceled.', reason);
          return;
      } else {
          const confirm = await $shell.FootPrintPortalManager.openConfirmationDialog('Cancel order', `Are you sure you want to cancel order ${$editor.entity.LookupCode}?`, 'Yes', 'No');
          if (confirm) {
              try {
  
                  if (orderStatusId === 1) {
  
                      const result = await $flows.SalesOrders.cancel_sales_order_flow({
                          orderId: orderId
                      });
                      const reason = result.reason;
  
                      if ($utils.isDefined(reason)) {
                          await $shell.FootPrintPortalManager.openErrorDialog('Order cannot be cancelled.', reason);
                          return;
                      }
                      else {
  
                          const title = 'Order Cancelled';
                          const message = `Order ${$editor.entity.LookupCode} successfully cancelled.`;
                          $shell.FootPrintPortalManager.openToaster(title, message, EToasterType.Success, { timeOut: 5000, positionClass: EToasterPosition.topRight });
                          await $editor.refresh();
                   
                      }
  
                  }
                  else {
                      const dialogResult = await $shell.FootPrintPortalManager.openshipping_order_cancellation_options_formDialog({
                          title: 'Select a reason code',
                          parentId: 45,
                          parentEntity: 'OperationType'
                      });
                      var userConfirmed = dialogResult.confirm;
                      var reasonCodeId = dialogResult.reasonCodeId;
  
                      if ($utils.isDefined(userConfirmed) && userConfirmed === false) {
  
                          return;
                      }
  
                      if (userConfirmed) {
  
                          const result = await $flows.SalesOrders.cancel_sales_order_shipment_and_order_lines_flow({
                              shipmentId: shipmentId,
                              reasonCodeId: reasonCodeId
                          });
                          const reason = result.reason;
  
                          if ($utils.isDefined(reason)) {
                              await $shell.FootPrintPortalManager.openErrorDialog('Order cannot be cancelled.', reason);
                              return;
                          }
                          else {
  
                              const title = 'Cancel Order';
                              const message = `Order ${$editor.entity.LookupCode} cancelled.`;
                              await $shell.FootPrintPortalManager.openInfoDialog(title, message);
                              await $editor.refresh();
                              await $editor.tabs.sales_order_lines.activate();
                          }
  
                      }
  
                  }
  
              } catch (error) {
                  const errorMessage = $utils.isDefined(error?.error?.error) ? error?.error?.error.message : error;
                  const errorDetail = $utils.isDefined(error?.error?.error) ? error?.error?.error : error;
                  const errorDescription = `Order ${$editor.entity.LookupCode} - ${errorMessage}`;
                  await $shell.FootPrintPortalManager.openErrorDialog('Cancel order error', 'An error occurred during the cancelling of the order', [errorDescription], null, [{ message: errorDescription, detail: errorDetail }]);
              }
          }
      }
  
  }
  else {
  
      throw new Error('Unable to determine the shipment for the selected order.')
  }
  }
  on_data_loaded(event = null) {
    return this.on_data_loadedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_data_loadedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  // Set editor title
  $editor.title = `Outbound Order ${$editor.entity.LookupCode}`;
  
  const orderLookup = $editor.entity.LookupCode;
  const owner = $editor.entity.Project?.Owner?.LookupCode;
  const project = $editor.entity.Project?.LookupCode
  const warehouse = $editor.entity.PreferredWarehouse?.Name
  const orderClass = $editor.entity.OrderClass?.LookupCode;
  
  $editor.fields.order_header.control.text = `${orderClass} ${orderLookup} for Project ${project} Warehouse ${warehouse}`
  
  var shipTo = '';
  
  const orderAddress = (await $flows.Addresses.get_address_single_string_by_orderId({ orderId: $editor.inParams.orderId }));
  if ($utils.isDefined(orderAddress)) {
      shipTo = orderAddress.address;
  
  }
  // Only display ship from if it is not an auto generated account
  if ($utils.isDefined(shipTo)) {
      if (!shipTo.startsWith('ord-')) {
  
          $editor.fields.ship_to.hidden = false;
          $editor.fields.ship_to.control.text = shipTo;
          $editor.fields.clear_ship_to.hidden = false;
  
      } else {
          $editor.fields.ship_to.hidden = true;
          $editor.fields.ship_to.control.text = '';
          $editor.fields.clear_ship_to.hidden = true;
      }
  }
  else {
      $editor.fields.ship_to.hidden = true;
      $editor.fields.ship_to.control.text = '';
      $editor.fields.clear_ship_to.hidden = true;
  }
  
  
  // Apply appointment text logic
  const dockAppointments = (await $datasources.DockAppointments.ds_get_dock_appointment_by_orderId.get({
      orderId: $editor.inParams.orderId
  })).result;
  
  if ($utils.isDefined(dockAppointments)) {
      $editor.toolbar.appointment.control.label = (await $flows.DockAppointments.get_formatted_dock_appointment({
          dockAppointmentId: dockAppointments[0]?.DockAppointmentId,
      })).formattedDockAppointment;
  
  } else {
      // Reset the appointment label back to original
      $editor.toolbar.appointment.control.styles.resetStyle();
      $editor.toolbar.appointment.control.label = 'Appointment'
  }
  
  // Discussions feature
  let discussionCheck = (await $operations.Discussions.Enable_Discussions_Feature.isAuthorized())
  
  if (discussionCheck) {
      const discussions = (await $datasources.Discussions.ds_storage_discussions.getList({
          entity_name: 'Order',
          entity_id: $editor.inParams.orderId.toString()
      })).result;
  
      if ($utils.isDefined(discussions)) {
  
          let containsUnread = false;
          let containsDiscussion = false;
  
          for (let discussion of discussions) {
              let read_on = discussion.read_on
              let discussionData = discussion.discussion
  
              if (!$utils.isDefined(read_on) && discussion.id != '0') {
                  containsUnread = true;
              }
              if ($utils.isDefined(discussionData) && discussion.id != '0') {
                  containsDiscussion = true;
              }
          }
          if (containsDiscussion) {
              if (containsUnread) {
                  $editor.toolbar.discussions.control.icon = "icon icon-ic_fluent_chat_multiple_20_regular";
                  $editor.toolbar.discussions.control.styles.setDestructiveClass();
              }
              else {
                  $editor.toolbar.discussions.control.icon = "icon icon-ic_fluent_chat_20_regular";
                  $editor.toolbar.discussions.control.styles.setDestructiveClass();
              }
          }
          else {
  
              $editor.toolbar.discussions.control.icon = "icon icon-ic_fluent_chat_empty_20_regular";
              $editor.toolbar.discussions.control.styles.setCreationClass();
  
          }
      }
  }
  else {
      $editor.toolbar.discussions.control.readOnly = true;
  }
  
  // Fetch shipments
  const shipments = (await $datasources.SalesOrders.ds_get_shipments_by_orderId.get({ orderId: $editor.entity.Id })).result;
  
  if (shipments.length > 1) {
      // close
      await $shell.SalesOrders.openErrorDialog('Invalid order', 'This order has more than one shipment. This editor cannot display this kind of order. Editor will close');
      $editor.close();
      return;
  }
  
  else {
  
  
      let stateId: number = 1;
      let has_manual: boolean = false;
      let stateName: string = $editor.entity.Status.Name;
  
      const orders = [{
          orderId: $editor.entity.Id,
          shipmentId: $editor.entity.ShipmentOrderLookups[0]?.ShipmentId
      }];
  
      const order_states = (await $flows.SalesOrders.get_sales_order_state({ orders: orders })).states;
  
      if (order_states.length > 0) {
          stateId = order_states[0].stateId;
          stateName = order_states[0].stateName;
          has_manual = order_states[0].has_manual;
      };
  
      $editor.vars.orderState = stateId;
      $editor.fields.order_state.control.text = stateName;
  
  
  
      // wait status
      if ($editor.entity.OrderStatusId === 128) {
          
          $editor.fields.order_state.control.text = 'Wait'
          $editor.toolbar.submit.hidden = false;
  
          $editor.toolbar.cancel.hidden = true;
          $editor.toolbar.on_delete.hidden = false;
          $editor.toolbar.on_delete.control.readOnly = false;
  
          $editor.fields.vendor_reference.control.readOnly = false;
          $editor.fields.carrier.control.readOnly = false;
          $editor.fields.expected_date.control.readOnly = false;
  
          // Widgets
          $editor.widgets.order_total_picked_gross_widget.hidden = true;
          $editor.widgets.order_total_picked_units_widget.hidden = true;
  
  
  
          $editor.tabs.shipping_contents.hidden = true;
          $editor.tabs.tasks.hidden = true;
  
      }
      // created status
      else if ($editor.entity.OrderStatusId === 1) {
  
          $editor.toolbar.submit.hidden = true;
  
          $editor.toolbar.cancel.hidden = false;
          $editor.toolbar.on_delete.hidden = false;
          $editor.toolbar.on_delete.control.readOnly = false;
  
          $editor.fields.vendor_reference.control.readOnly = false;
          $editor.fields.carrier.control.readOnly = false;
          $editor.fields.expected_date.control.readOnly = false;
  
          // Widgets
          $editor.widgets.order_total_picked_gross_widget.hidden = true;
          $editor.widgets.order_total_picked_units_widget.hidden = true;
  
  
  
          $editor.tabs.shipping_contents.hidden = true;
          $editor.tabs.tasks.hidden = true;
  
  
  
      } else if ($editor.entity.OrderStatusId === 2) {
          // processing status
  
          $editor.toolbar.submit.hidden = true;
  
          $editor.fields.vendor_reference.control.readOnly = false;
          $editor.fields.carrier.control.readOnly = false;
          $editor.fields.expected_date.control.readOnly = false;
  
          $editor.tabs.tasks.hidden = false;
  
  
          const shipmentContents = (await $datasources.SalesOrders.ds_get_shipmentscontents_by_orderId_shipmentId_top1.get({
              orderId: $editor.inParams.orderId,
              shipmentId: $editor.entity.ShipmentOrderLookups[0]?.ShipmentId
          })).result;
  
          // Revert button and Shipping Contents 'Picked' tab
          if ($utils.isDefined(shipmentContents)) {
  
              $editor.tabs.shipping_contents.hidden = false;
  
          } else {
  
              $editor.tabs.shipping_contents.hidden = true;
  
          }
  
          // Set manual allocations
          var manualAllocations;
          if ($utils.isDefined(has_manual)) {
  
              if (has_manual) {
  
                  manualAllocations = true;
              } else {
                  manualAllocations = false;
  
              }
          } else {
              manualAllocations = false;
  
          }
  
          if ($utils.isDefined(stateId)) {
              switch (stateId) {
  
                  case 2: {
                      $editor.toolbar.cancel.hidden = false;
                      $editor.toolbar.cancel.control.readOnly = false;
  
                      $editor.toolbar.on_delete.hidden = true;
  
  
                      break;
                  }
                  case 3: {
                      $editor.toolbar.cancel.hidden = false;
                      $editor.toolbar.cancel.control.readOnly = false;
  
  
                      $editor.toolbar.on_delete.hidden = true;
  
                      break;
                  }
                  case 4: {
                      $editor.toolbar.cancel.hidden = false;
                      $editor.toolbar.cancel.control.readOnly = false;
  
                      $editor.toolbar.on_delete.control.readOnly = true;
  
                      break;
                  }
                  // All pick tasks in a released status
                  case 5: {
                      $editor.toolbar.cancel.hidden = false;
                      $editor.toolbar.cancel.control.readOnly = false;
  
  
                      $editor.toolbar.on_delete.control.readOnly = true;
  
  
                      break;
                  } // One or more pick tasks completed and one or more pick tasks released
                  case 6: {
  
                      $editor.toolbar.cancel.control.readOnly = true;
                      $editor.toolbar.on_delete.control.readOnly = true;
  
  
                      break;
                  } // No released pick tasks exist and one or more shipping licenseplates require a loading task
                  case 7: {
  
  
                      $editor.toolbar.cancel.control.readOnly = true;
                      $editor.toolbar.on_delete.control.readOnly = true;
  
                      break;
                  } // No released pick tasks or manual allocations exist and all shipping licenseplates loaded
                  case 8: {
                      if (manualAllocations) {
  
                      } else {
  
                      }
  
                      $editor.toolbar.cancel.control.readOnly = true;
                      $editor.toolbar.on_delete.control.readOnly = true;
  
  
                      break;
                  }
                  // No released pick tasks or manual allocations exist and all shipping contents unpicked
                  case 9: {
                      if (manualAllocations) {
  
                          $editor.toolbar.cancel.hidden = false;
                          $editor.toolbar.cancel.control.readOnly = true;
                      } else {
  
                          $editor.toolbar.cancel.hidden = false;
                          $editor.toolbar.cancel.control.readOnly = false;
                      }
  
  
                      $editor.toolbar.on_delete.control.readOnly = true;
  
  
                      break;
                  }
              }
          } else {
  
  
          }
          // Widgets
          $editor.widgets.order_total_picked_gross_widget.hidden = false;
          $editor.widgets.order_total_picked_units_widget.hidden = false;
  
      } else {
  
          $editor.toolbar.submit.hidden = true;
  
          $editor.toolbar.cancel.hidden = true;
          $editor.toolbar.on_delete.hidden = true;
  
          $editor.fields.vendor_reference.control.readOnly = true;
          $editor.fields.carrier.control.readOnly = true;
  
          $editor.fields.expected_date.control.readOnly = true;
  
          // Widgets
          $editor.widgets.order_total_picked_gross_widget.hidden = false;
          $editor.widgets.order_total_picked_units_widget.hidden = false;
  
  
  
          $editor.tabs.shipping_contents.hidden = false;
  
          $editor.tabs.tasks.hidden = false;
  
      }
  }
  
  // Apply operations
  await $editor.on_apply_operations();
  
  }
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  $editor.tabs.shipping_contents.hidden = true;
  
  
  // Set Date/Time Formats
  $editor.fields.expected_date.control.format = `${$settings.FootPrintPortalManager.DateFormat}, ${$settings.FootPrintPortalManager.TimeFormat.toUpperCase() == '24 HOUR' ? 'HH:mm' : 'LT'}`;
  
  // Create shipment if none found
  const shipments = (await $datasources.SalesOrders.ds_get_shipments_by_orderId.get({ orderId: $editor.inParams.orderId })).result;
  if (!$utils.isDefined(shipments)) {
  
      const nextShipmentId = (await $flows.Utilities.reserve_nextId_flow({ entity: 'Shipment' })).nextId;
  
      const order = (await $datasources.SalesOrders.ds_get_order_by_orderId.get({ orderId: $editor.inParams.orderId })).result;
  
      if ($utils.isAllDefined(order, order.PreferredWarehouseId)) {
  
          // create shipment order lookup
          const payload = {
              "OrderId": $editor.inParams.orderId,
              "Shipment": {
                  "Id": nextShipmentId,
                  "LookupCode": order.LookupCode,
                  "CarrierId": order.PreferredCarrierId,
                  "CarrierServiceTypeId": order.PreferredCarrierServiceTypeId,
                  "ExpectedWarehouseId": order.PreferredWarehouseId,
                  "ExpectedDate": order.RequestedDeliveryDate,
                  "Cartonized": false,
                  "ConfirmSeal": false,
                  "StatusId": 1,
                  "Transhipment": false,
                  "TypeId": 2
              }
          }
          await $flows.Utilities.crud_create_flow({ entitySet: 'ShipmentOrderLookup', entity: payload });
  
          $editor.vars.shipmentId = nextShipmentId;
      }
  }
  else {
      $editor.vars.shipmentId = shipments[0]?.ShipmentId;
  }
  
  // Set warehouse on order if blank and shipment contains expected warehouse
  const orders = (await $datasources.SalesOrders.ds_get_order_by_orderId.get({ orderId: $editor.inParams.orderId })).result;
  if (!$utils.isDefined(orders.PreferredWarehouseId) && $utils.isDefined(shipments[0]?.Shipment?.ExpectedWarehouseId)) {
  
      let payload: any = {};
  
      payload.PreferredWarehouseId = shipments[0]?.Shipment?.ExpectedWarehouseId;
  
      await $flows.Utilities.crud_update_flow({ entitySet: 'Orders', id: $editor.inParams.orderId, entity: payload });
  }
  }
  on_save(event = null) {
    return this.on_saveInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_saveInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  var shipmentId = $editor.vars.shipmentId;
  
  
  let payload: any = {};
  
  if ($editor.fields.reference.control.isChanged) {
      payload.OwnerReference = $editor.fields.reference.control.value;
  }
  if ($editor.fields.vendor_reference.control.isChanged) {
      payload.VendorReference = $editor.fields.vendor_reference.control.value;
  }
  if ($editor.fields.carrier.control.isChanged) {
      payload.PreferredCarrierId = $editor.fields.carrier.control.value;
      // Clear the carrier service type as it could conflict with the newly selected carrier
      payload.PreferredCarrierServiceTypeId = null;
      $editor.fields.carrier_service.control.value = null;
      $editor.fields.carrier_service.control.displayText = null;
  
  }
  if ($editor.fields.carrier_service.control.isChanged) {
      payload.PreferredCarrierServiceTypeId = $editor.fields.carrier_service.control.value;
  }
  
  if ($editor.fields.expected_date.control.isChanged) {
      payload.RequestedDeliveryDate = $editor.fields.expected_date.control.value;
  }
  if ($editor.fields.notes.control.isChanged) {
      payload.Notes = $editor.fields.notes.control.value;
  }
  
  
  
  await $flows.Utilities.crud_update_flow({ entitySet: 'Orders', id: $editor.entity.Id, entity: payload });
  
  
  // Create the shipment if it does not exist only if the order has a preferred warehouse of if the preferred warehouse is selected in the editor
  if (!$utils.isDefined(shipmentId)) {
  
      const nextShipmentId = (await $flows.Utilities.reserve_nextId_flow({ entity: 'Shipment' })).nextId;
  
      const order = (await $datasources.SalesOrders.ds_get_order_by_orderId.get({ orderId: $editor.inParams.orderId })).result;
  
  
  
      if ($utils.isAllDefined(order, order.PreferredWarehouseId) ) {
  
          // create shipment order lookup
          const payload = {
              "OrderId": $editor.inParams.orderId,
              "Shipment": {
                  "Id": nextShipmentId,
                  "LookupCode": order.LookupCode,
                  "CarrierId": order.PreferredCarrierId,
                  "CarrierServiceTypeId": order.PreferredCarrierServiceTypeId,
                  "ExpectedWarehouseId": order.PreferredWarehouseId,
                  "ExpectedDate": order.RequestedDeliveryDate,
                  "Cartonized": false,
                  "ConfirmSeal": false,
                  "StatusId": 1,
                  "Transhipment": false,
                  "TypeId": 2
              }
          }
          await $flows.Utilities.crud_create_flow({ entitySet: 'ShipmentOrderLookup', entity: payload });
  
          shipmentId = nextShipmentId;
          $editor.vars.shipmentId = nextShipmentId;
  
      }
  
  
  }
  
  if ($utils.isDefined(shipmentId)) {
  
      payload = {};
  
  
      if ($editor.fields.carrier.control.isChanged) {
          payload.CarrierId = $editor.fields.carrier.control.value;
          // Clear the carrier service type as it could conflict with the newly selected carrier
          payload.CarrierServiceTypeId = null;
          $editor.fields.carrier_service.control.value = null;
          $editor.fields.carrier_service.control.displayText = null;
      }
      if ($editor.fields.carrier_service.control.isChanged) {
          payload.CarrierServiceTypeId = $editor.fields.carrier_service.control.value;
      }
  
      if ($editor.fields.expected_date.control.isChanged) {
          payload.ExpectedDate = $editor.fields.expected_date.control.value;
      }
      if ($editor.fields.container_identifier.control.isChanged) {
          payload.ContainerIdentifier = $editor.fields.container_identifier.control.value;
      }
  
      if ($editor.fields.notes.control.isChanged) {
          payload.Notes = $editor.fields.notes.control.value;
      }
  
      await $flows.Utilities.crud_update_flow({ entitySet: 'Shipments', id: $editor.vars.shipmentId, entity: payload });
  
  
  }
  
  await $editor.refresh();
  }
  on_appointment_clicked(event = null) {
    return this.on_appointment_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_appointment_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  const dockAppointments = (await $datasources.DockAppointments.ds_get_dock_appointment_by_orderId.get({
      orderId: $editor.inParams.orderId
  })).result;
  
  if ($utils.isDefined(dockAppointments)) {
  
      const dialogResult = await $shell.FootPrintPortalManager.opendock_appointment_editorDialog({
          dockAppointmentId: dockAppointments[0].DockAppointmentId
      })
  
  } else {
        // Check if the selected order is in a rejected status and throw an error.
      if ($editor.entity.OrderStatusId == 4096) {
  
          throw new Error('Selected order is currently in a rejected status, a dock appointment cannot be created against rejected orders.')
      }
  
      const nextAppointmentId = (await $flows.Utilities.reserve_nextId_flow({ entity: 'DockAppointment' })).nextId;
  
      const dialogResult = await $shell.FootPrintPortalManager.opendock_appointment_creation_formDialog({
         
          warehouseId: [$editor.entity.PreferredWarehouseId],
          lookupcode: nextAppointmentId.toString(),
          scheduledArrival: $utils.isDefined($editor.entity.RequestedDeliveryDate) ? $editor.entity.RequestedDeliveryDate : $utils.date.now(),
          scheduledDeparture: $utils.isDefined($editor.entity.RequestedDeliveryDate) ? $utils.date.add(1, 'hour', $editor.entity.RequestedDeliveryDate) : $utils.date.add(1, 'hour', $utils.date.now()),
          typeId: 2,
          ownerId:  $editor.entity.Project.OwnerId,
          projectId: $editor.entity.ProjectId,
          carrierId: $editor.entity.PreferredCarrierId,
          orderId: $editor.inParams.orderId
      });
  
  }
  
  // Always refresh as the user might have made some changes to an existing address
  await $editor.refresh();
  
  }
  on_print_clicked(event = null) {
    return this.on_print_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_print_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  
  await $shell.SalesOrders.openload_slip_reportDialog({ orderId: $editor.inParams.orderId });
  }
  on_attachments_clicked(event = null) {
    return this.on_attachments_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_attachments_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  await $shell.FootPrintPortalManager.openentity_attachments_gridDialog({ entityType: 'Order', entityKeys: [{ name: 'Id', value: $editor.entity.Id }]});
  }
  on_discussions_clicked(event = null) {
    return this.on_discussions_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_discussions_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  await $shell.Discussions.opendiscussions_listDialog({
      entity_name: 'Order',
      entity_id: $editor.inParams.orderId.toString()
  },
  'flyout')
  
  $editor.refresh();
  }
  on_apply_operations(event = null) {
    return this.on_apply_operationsInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_apply_operationsInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Update.isAuthorized()) {
      $editor.fields.carrier.control.readOnly = true;
      $editor.fields.expected_date.control.readOnly = true;
      $editor.fields.notes.control.readOnly = true;
      $editor.fields.reference.control.readOnly = true;
      $editor.fields.carrier_service.control.readOnly = true;
      $editor.fields.vendor_reference.control.readOnly = true;
      $editor.fields.container_identifier.control.readOnly = true;
  }
  
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Cancel.isAuthorized()) {
      $editor.toolbar.cancel.control.readOnly = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Delete.isAuthorized()) {
      $editor.toolbar.on_delete.control.readOnly = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Print.isAuthorized()) {
      $editor.toolbar.print.control.readOnly = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Attachments.isAuthorized()) {
      $editor.toolbar.attachments.control.readOnly = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Appointments.isAuthorized()) {
      $editor.toolbar.appointment.control.readOnly = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Activity.isAuthorized()) {
      $editor.tabs.tasks.hidden = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Contacts.isAuthorized()) {
      $editor.tabs.contacts.hidden = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Instructions.isAuthorized()) {
      $editor.tabs.instructions.hidden = true;
  }
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_Picked.isAuthorized()) {
      $editor.tabs.shipping_contents.hidden = true;
      $editor.widgets.order_total_picked_gross_widget.hidden = true;
      $editor.widgets.order_total_picked_units_widget.hidden = true;
  }
  
  if (await $operations.FootPrintPortalManager.Disable_ShippingOrder_ManageShipToContacts.isAuthorized()) {
      $editor.toolbar.options.control.buttons.ship_to.readOnly = true;
      $editor.fields.clear_ship_to.hidden = true;
  }
  
  
  }
  on_submit_order(event = null) {
    return this.on_submit_orderInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_submit_orderInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  const orderLookup = $editor.entity.LookupCode;
  
  const validate = await $shell.FootPrintPortalManager.openConfirmationDialog('Submit order', `Are you sure you want to submit the following order ${orderLookup} to the warehouse?`, 'Yes', 'No');
  if (validate) {
      try {
  
          let payload: any = {};
          payload.OrderStatusId = 1;
  
          await $flows.Utilities.crud_update_flow({ entitySet: 'Orders', id: $editor.entity.Id, entity: payload });
  
          const title = 'Order Submitted'
          const message = `Order ${orderLookup} successfully submitted. `
          $shell.FootPrintPortalManager.openToaster(title, message, EToasterType.Success, { timeOut: 5000, positionClass: EToasterPosition.topRight });
          $editor.refresh();
  
  
      } catch (error) {
          const errorMessage = $utils.isDefined(error?.error?.error) ? error?.error?.error.message : error;
          const errorDetail = $utils.isDefined(error?.error?.error) ? error?.error?.error : error;
          const errorDescription = `Order ${$editor.entity.LookupCode} - ${errorMessage}`;
          await $shell.FootPrintPortalManager.openErrorDialog('Submit order error', 'An error occurred during the submission of the order', [errorDescription], null, [{ message: errorDescription, detail: errorDetail }]);
      }
  }
  }
  on_ship_to_clicked(event = null) {
    return this.on_ship_to_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_ship_to_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  const dialogResult = await $shell.FootPrintPortalManager.openaccounts_gridDialog({
      ownerId: $editor.entity.Project.OwnerId,
      typeIds: [2, 4, 5], // Shipping, Residential, Commercial
      accountTypeId: 2, // Customer
      allowSelection: true
  });
  
  const userConfirmed = dialogResult.confirm;
  const accountId = dialogResult.accountId;
  const addressId = dialogResult.addressId;
  const contactId = dialogResult.contactId;
  const ownerId = dialogResult.ownerId;
  
  if (userConfirmed && $utils.isAllDefined(accountId, addressId)) {
      let payload: any = {};
  
      payload.AccountId = accountId;
      payload.BillingAddresId = addressId;
      await $flows.Utilities.crud_update_flow({ entitySet: 'Orders', id: $editor.entity.Id, entity: payload });
  
      if (ownerId === 0) {
  
          const owner_account = (await $datasources.Addresses.ds_find_owner_account_lookup.get({ ownerId: $editor.entity.Project.OwnerId, accountId: accountId })).result;
  
          if (!$utils.isDefined(owner_account)) {
              // OwnersAccounts
              const payload = {
                  "OwnerId": $editor.entity.Project.OwnerId,
                  "AccountId": accountId,
                  "IsDefaultAccount": false
              }
              await $flows.Utilities.crud_create_flow({ entitySet: 'OwnersAccountsLookup', entity: payload });
          }
      }
  
      if ($utils.isDefined($editor.entity.ShipmentOrderLookups)) {
          const shipmentId = $editor.entity.ShipmentOrderLookups[0].ShipmentId;
          let shipment_payload: any = {};
  
          shipment_payload.AccountId = accountId;
          shipment_payload.ContactId = contactId;
  
          await $flows.Utilities.crud_update_flow({ entitySet: 'Shipments', id: shipmentId, entity: shipment_payload });
      }
  }
  // Always refresh as the user might have made some changes to an existing address
  await $editor.refresh();
  }
  on_ship_to_cleared(event = null) {
    return this.on_ship_to_clearedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_ship_to_clearedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  const orderAddress = (await $flows.Addresses.create_generic_order_address_flow({ orderId: $editor.inParams.orderId }));
  if ($utils.isDefined(orderAddress)) {
  
      var accountId = orderAddress.accountId;
      var addressId = orderAddress.addressId;
  
  }
  
  if ($utils.isAllDefined(accountId, addressId)) {
  
  
      let payload: any = {};
  
      payload.AccountId = accountId;
      payload.BillingAddresId = addressId;
  
      await $flows.Utilities.crud_update_flow({ entitySet: 'Orders', id: $editor.entity.Id, entity: payload });
  
  
  
      await $editor.refresh();
  
  
  }
  else {
  
      throw new Error('Unable to clear the selected ship to account.')
  }
  }
  on_email_requests_clicked(event = null) {
    return this.on_email_requests_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_email_requests_clickedInternal(
    $editor: FootPrintPortalManager_shipping_order_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  await $shell.Notifications.openemail_requests_gridDialog({
      entityType: 'Order',
      entityId: $editor.entity.Id
  }, 'modal');
  }
  //#endregion private flows
  
}
